import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Building2, ShieldCheck } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { LOGO_URL } from "@/components/Brand";

const timeline = [
  ["2021", "Harguon Enterprises registered", "The parent company is incorporated in Haryana with GSTIN 06ATUPP9372R1Z9."],
  ["2023", "First 10 client payrolls", "The team quietly runs payroll for its first ten client companies across Delhi NCR."],
  ["2025", "HG Consultancy launched", "The payroll servicing arm of Harguaon Group becomes a standalone service line."],
  ["2026", "HarguonPayroll.com software rolled out", "Every client and their employees log in to a private portal — payslips, attendance and calculators, all in one place."],
];

const credentials = [
  { icon: Building2, label: "GSTIN", value: "06ATUPP9372R1Z9" },
  { icon: Award, label: "State code 06", value: "Haryana, India" },
  { icon: ShieldCheck, label: "Compliance", value: "FY 2025-26 · New Regime" },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#FAF7EF] text-neutral-950">
      <SiteNav />

      {/* Hero */}
      <section className="border-b border-[#E8DFC5] bg-grid relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{background: "radial-gradient(900px 500px at 20% 0%, rgba(201,169,97,0.18), transparent 60%)"}}></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-6 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#C9A961]"></span>
              About · who we are
            </div>
            <h1 className="font-serif font-black text-[52px] leading-[0.98] md:text-[80px] tracking-[-0.03em]" data-testid="about-title">
              A payroll house<br />
              <span className="italic text-gold-gradient">built on trust.</span>
            </h1>
            <p className="mt-8 text-lg text-neutral-700 max-w-2xl leading-relaxed">
              HG Consultancy is the payroll servicing arm of Harguaon Group. We handle payroll, attendance and statutory compliance for
              small and mid-sized businesses across India — so founders can focus on the work that actually grows their company.
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-[#C9A961] bg-white p-8 flex flex-col items-center text-center">
              <img src={LOGO_URL} alt="HG Consultancy" className="h-32 w-auto mb-4" />
              <div className="font-serif font-bold text-lg tracking-tight">Harguaon Group</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E] mt-1">Est. Payroll Servicing</div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">§ 01 — Story</div>
            <h2 className="font-serif font-black text-4xl md:text-5xl tracking-[-0.02em] leading-[0.95]">
              Why we<br /><span className="italic text-gold-gradient">exist.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-lg text-neutral-700 leading-relaxed">
            <p>
              Every founder we've worked with has had the same complaint — payroll eats a Sunday every month, the accountant forgets
              a statutory filing, and no one really knows if the employee got the right amount.
            </p>
            <p>
              We built HG Consultancy to make that noise disappear. We take on the entire payroll cycle — from attendance to
              itemised payslips to statutory returns — and hand you back a clean deliverable each month with a signed sign-off.
            </p>
            <p>
              Everything runs on our own software, <em className="text-[#8C6F1E]">Harguon Payroll</em>, which every one of your employees can log into
              to see their own attendance, payslip history, and salary calculator.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="border-b border-[#E8DFC5] bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#D4B87D] mb-3">§ 02 — Timeline</div>
          <h2 className="font-serif font-black text-4xl md:text-5xl tracking-[-0.02em] mb-16 max-w-3xl leading-[0.95]">
            The road so far.
          </h2>
          <div className="border-t border-[#2a2416]">
            {timeline.map(([year, title, body]) => (
              <div key={year} className="grid md:grid-cols-12 gap-6 py-8 border-b border-[#2a2416]" data-testid={`timeline-${year}`}>
                <div className="md:col-span-2 font-serif italic text-[#C9A961] text-3xl">{year}</div>
                <div className="md:col-span-4 font-serif font-bold text-2xl tracking-tight">{title}</div>
                <div className="md:col-span-6 text-neutral-400 leading-relaxed">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#8C6F1E] mb-3">§ 03 — Credentials</div>
          <h2 className="font-serif font-black text-4xl md:text-5xl tracking-[-0.02em] mb-12 max-w-3xl leading-[0.95]">
            Registered.<br /><span className="italic text-gold-gradient">Compliant.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {credentials.map((c) => (
              <div key={c.label} className="border border-[#E8DFC5] bg-white p-8" data-testid={`cred-${c.label.toLowerCase()}`}>
                <c.icon className="w-6 h-6 mb-4 text-[#8C6F1E]" strokeWidth={1.5} />
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{c.label}</div>
                <div className="mt-1 font-serif font-bold text-2xl tracking-tight">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[#E8DFC5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="font-serif font-black text-4xl md:text-5xl tracking-[-0.02em] leading-[0.95]">
              Talk to us about<br /><span className="italic text-gold-gradient">your team.</span>
            </h3>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-6 py-4 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
              Book a call <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 border border-[#C9A961] px-6 py-4 font-mono text-sm hover:bg-neutral-950 hover:text-[#D4B87D] transition-colors">
              See services
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
